import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Wrench, Plus, Search, Filter, AlertTriangle, CheckCircle2,
  Clock, Settings, Edit, Trash2, Calendar, ChevronDown, ChevronRight,
  Home, Download, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaintenanceModule = ({ onClose }) => {
  const { user, isAdmin } = useAuth();
  const [config, setConfig] = useState(null);
  const [machines, setMachines] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [dueThisWeek, setDueThisWeek] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [sectionFilter, setSectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [showMachineDialog, setShowMachineDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Forms
  const [machineForm, setMachineForm] = useState({
    machine_name: '', machine_number: '', section: '', manufacturer: '',
    model: '', serial_number: '', location: '', criticality: 'Medium', notes: ''
  });
  const [activityForm, setActivityForm] = useState({
    activity_name: '', description: '', frequency: 'Monthly', custom_interval_days: '',
    next_due_date: '', responsible_person: '', estimated_downtime: '',
    required_spares: '', safety_requirements: ''
  });
  const [completeForm, setCompleteForm] = useState({
    completion_date: new Date().toISOString().split('T')[0],
    completed_by: '', notes: '', parts_used: '', downtime_experienced: ''
  });

  // View mode
  const [viewMode, setViewMode] = useState('dashboard'); // dashboard, machines, activities

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [configRes, machinesRes, statsRes, dueRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/maintenance/config`),
        axios.get(`${BACKEND_URL}/api/maintenance/machines${sectionFilter ? `?section=${encodeURIComponent(sectionFilter)}` : ''}`),
        axios.get(`${BACKEND_URL}/api/maintenance/dashboard/stats`),
        axios.get(`${BACKEND_URL}/api/maintenance/dashboard/due-this-week`)
      ]);
      setConfig(configRes.data);
      setMachines(machinesRes.data);
      setStats(statsRes.data);
      setDueThisWeek(dueRes.data.due_this_week);
      setOverdue(dueRes.data.overdue);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async (machineId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/maintenance/activities${machineId ? `?machine_id=${machineId}` : ''}`);
      setActivities(res.data);
    } catch { setActivities([]); }
  };

  const handleSaveMachine = async () => {
    if (!machineForm.machine_name || !machineForm.section) {
      toast.error('Machine name and section are required'); return;
    }
    try {
      if (editingMachine) {
        await axios.put(`${BACKEND_URL}/api/maintenance/machines/${editingMachine.machine_id}`, machineForm);
        toast.success('Machine updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/maintenance/machines`, { ...machineForm, created_by: user?.email || '' });
        toast.success('Machine added');
      }
      setShowMachineDialog(false);
      setEditingMachine(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save machine');
    }
  };

  const handleSaveActivity = async () => {
    if (!activityForm.activity_name || !selectedMachine) {
      toast.error('Activity name is required'); return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/maintenance/activities`, {
        ...activityForm,
        machine_id: selectedMachine.machine_id,
        custom_interval_days: activityForm.custom_interval_days ? parseInt(activityForm.custom_interval_days) : null,
        created_by: user?.email || ''
      });
      toast.success('Maintenance activity added');
      setShowActivityDialog(false);
      fetchActivities(selectedMachine.machine_id);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save activity');
    }
  };

  const handleCompleteActivity = async () => {
    if (!selectedActivity || !completeForm.completion_date || !completeForm.completed_by) {
      toast.error('Completion date and person are required'); return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/maintenance/activities/${selectedActivity.activity_id}/complete`, completeForm);
      toast.success('Maintenance activity completed - next due date calculated');
      setShowCompleteDialog(false);
      setSelectedActivity(null);
      fetchAll();
      if (selectedMachine) fetchActivities(selectedMachine.machine_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete activity');
    }
  };

  const handleDeactivateMachine = async (machine) => {
    if (!window.confirm(`Deactivate ${machine.machine_name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/maintenance/machines/${machine.machine_id}`);
      toast.success(`${machine.machine_name} deactivated`);
      fetchAll();
    } catch { toast.error('Failed to deactivate machine'); }
  };

  const handleExport = async () => {
    try {
      const [activitiesRes, historyRes, machinesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/maintenance/activities`),
        axios.get(`${BACKEND_URL}/api/maintenance/history?limit=5000`),
        axios.get(`${BACKEND_URL}/api/maintenance/machines`)
      ]);

      const machineMap = {};
      machinesRes.data.forEach(m => { machineMap[m.machine_id] = m; });

      const wb = XLSX.utils.book_new();

      // Sheet 1: All Activities (schedule)
      const actRows = [['Section', 'Machine', 'Machine Number', 'Activity', 'Frequency', 'Last Done', 'Next Due', 'Status', 'Responsible', 'Est. Downtime', 'Spares Required', 'Safety']];
      activitiesRes.data.forEach(a => {
        const m = machineMap[a.machine_id] || {};
        actRows.push([m.section || '', m.machine_name || '', m.machine_number || '', a.activity_name, a.frequency, a.last_maintenance_date?.slice(0, 10) || '', a.next_due_date?.slice(0, 10) || '', a.status, a.responsible_person || '', a.estimated_downtime || '', a.required_spares || '', a.safety_requirements || '']);
      });
      const actSheet = XLSX.utils.aoa_to_sheet(actRows);
      actSheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 12 }, { wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, actSheet, 'Maintenance Schedule');

      // Sheet 2: Completed Work History
      const histRows = [['Section', 'Machine', 'Activity', 'Completion Date', 'Completed By', 'Notes', 'Parts Used', 'Downtime']];
      historyRes.data.forEach(h => {
        const m = machineMap[h.machine_id] || {};
        histRows.push([m.section || '', m.machine_name || h.machine_id, h.activity_name, h.completion_date?.slice(0, 10) || '', h.completed_by || '', h.notes || '', h.parts_used || '', h.downtime_experienced || '']);
      });
      const histSheet = XLSX.utils.aoa_to_sheet(histRows);
      histSheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 28 }, { wch: 14 }, { wch: 22 }, { wch: 30 }, { wch: 22 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, histSheet, 'Completed Work');

      // Sheet 3: Machine Register
      const machRows = [['Section', 'Machine Name', 'Machine Number', 'Manufacturer', 'Model', 'Serial Number', 'Location', 'Criticality', 'Status']];
      machinesRes.data.forEach(m => {
        machRows.push([m.section, m.machine_name, m.machine_number || '', m.manufacturer || '', m.model || '', m.serial_number || '', m.location || '', m.criticality, m.status]);
      });
      const machSheet = XLSX.utils.aoa_to_sheet(machRows);
      machSheet['!cols'] = [{ wch: 20 }, { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, machSheet, 'Machine Register');

      XLSX.writeFile(wb, `Maintenance_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Maintenance report exported');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const openEditMachine = (machine) => {
    setEditingMachine(machine);
    setMachineForm({
      machine_name: machine.machine_name, machine_number: machine.machine_number || '',
      section: machine.section, manufacturer: machine.manufacturer || '',
      model: machine.model || '', serial_number: machine.serial_number || '',
      location: machine.location || '', criticality: machine.criticality || 'Medium',
      notes: machine.notes || ''
    });
    setShowMachineDialog(true);
  };

  const openAddMachine = () => {
    setEditingMachine(null);
    setMachineForm({ machine_name: '', machine_number: '', section: sectionFilter || '', manufacturer: '', model: '', serial_number: '', location: '', criticality: 'Medium', notes: '' });
    setShowMachineDialog(true);
  };

  const openAddActivity = (machine) => {
    setSelectedMachine(machine);
    setActivityForm({ activity_name: '', description: '', frequency: 'Monthly', custom_interval_days: '', next_due_date: '', responsible_person: '', estimated_downtime: '', required_spares: '', safety_requirements: '' });
    setShowActivityDialog(true);
  };

  const openComplete = (activity) => {
    setSelectedActivity(activity);
    setCompleteForm({ completion_date: new Date().toISOString().split('T')[0], completed_by: user?.email || '', notes: '', parts_used: '', downtime_experienced: '' });
    setShowCompleteDialog(true);
  };

  const filteredActivities = useMemo(() => activities.filter(a => {
    if (statusFilter && a.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return a.activity_name?.toLowerCase().includes(q) || a.responsible_person?.toLowerCase().includes(q);
    }
    return true;
  }), [activities, statusFilter, searchQuery]);

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-8 h-8 animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white uppercase tracking-wide">Maintenance Module</h2>
            <p className="text-xs text-slate-500">Planned Preventative Maintenance</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleExport} variant="outline" className="border-slate-700 text-slate-300" data-testid="export-maintenance-btn">
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
          {isAdmin() && (
            <Button size="sm" onClick={openAddMachine} className="bg-amber-500/20 text-amber-400 border border-amber-500/50" data-testid="add-machine-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Machine
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500 uppercase">Machines</p>
            <p className="text-2xl font-bold font-mono text-white">{stats.total_machines}</p>
          </div>
          <div className="bg-slate-900/60 border border-red-500/20 rounded-lg p-3 text-center cursor-pointer" onClick={() => setViewMode('dashboard')}>
            <p className="text-xs text-red-400 uppercase">Overdue</p>
            <p className="text-2xl font-bold font-mono text-red-400">{stats.overdue}</p>
          </div>
          <div className="bg-slate-900/60 border border-orange-500/20 rounded-lg p-3 text-center cursor-pointer" onClick={() => setViewMode('dashboard')}>
            <p className="text-xs text-orange-400 uppercase">Due This Week</p>
            <p className="text-2xl font-bold font-mono text-orange-400">{stats.due_this_week}</p>
          </div>
          <div className="bg-slate-900/60 border border-cyan-500/20 rounded-lg p-3 text-center">
            <p className="text-xs text-cyan-400 uppercase">Upcoming</p>
            <p className="text-2xl font-bold font-mono text-cyan-400">{stats.upcoming}</p>
          </div>
          <div className="bg-slate-900/60 border border-green-500/20 rounded-lg p-3 text-center">
            <p className="text-xs text-green-400 uppercase">Completed</p>
            <p className="text-2xl font-bold font-mono text-green-400">{stats.completed_total}</p>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        {['dashboard', 'machines'].map(mode => (
          <Button key={mode} size="sm" onClick={() => { setViewMode(mode); if (mode === 'machines') fetchAll(); }}
            className={`text-xs uppercase tracking-wide ${viewMode === mode ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' : 'bg-slate-900/40 text-slate-500 border-slate-800'} border`}>
            {mode === 'dashboard' ? 'Due This Week' : 'Machines & Activities'}
          </Button>
        ))}
        {/* Section filter */}
        <Select value={sectionFilter || 'all'} onValueChange={(val) => { setSectionFilter(val === 'all' ? '' : val); }}>
          <SelectTrigger className="w-[200px] h-8 bg-slate-900/60 border-slate-800 text-xs">
            <SelectValue placeholder="All Sections" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sections</SelectItem>
            {config?.sections?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* DASHBOARD VIEW - Due This Week */}
      {viewMode === 'dashboard' && (
        <div className="space-y-4">
          {overdue.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Overdue ({overdue.length})
              </h3>
              <div className="space-y-2">
                {overdue.filter(a => !sectionFilter || a.section === sectionFilter).map(act => (
                  <div key={act.activity_id} className="bg-red-950/20 border border-red-500/20 rounded-lg p-3 flex items-center justify-between" data-testid={`overdue-${act.activity_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">OVERDUE</span>
                        <span className="text-white font-bold">{act.activity_name}</span>
                      </div>
                      <div className="flex gap-4 mt-1 text-xs text-slate-400">
                        <span>{act.section}</span>
                        <span>{act.machine_name}</span>
                        <span>Due: {act.next_due_date?.slice(0, 10)}</span>
                        {act.responsible_person && <span>Resp: {act.responsible_person}</span>}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => openComplete(act)} className="bg-green-500/20 text-green-400 border border-green-500/50 text-xs" data-testid={`complete-${act.activity_id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Complete
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {dueThisWeek.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-orange-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Due This Week ({dueThisWeek.length})
              </h3>
              <div className="space-y-2">
                {dueThisWeek.filter(a => !sectionFilter || a.section === sectionFilter).map(act => (
                  <div key={act.activity_id} className="bg-orange-950/20 border border-orange-500/20 rounded-lg p-3 flex items-center justify-between" data-testid={`due-${act.activity_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 font-bold">DUE</span>
                        <span className="text-white font-bold">{act.activity_name}</span>
                      </div>
                      <div className="flex gap-4 mt-1 text-xs text-slate-400">
                        <span>{act.section}</span>
                        <span>{act.machine_name}</span>
                        <span>Due: {act.next_due_date?.slice(0, 10)}</span>
                        {act.responsible_person && <span>Resp: {act.responsible_person}</span>}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => openComplete(act)} className="bg-green-500/20 text-green-400 border border-green-500/50 text-xs" data-testid={`complete-${act.activity_id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Complete
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {overdue.length === 0 && dueThisWeek.length === 0 && (
            <div className="text-center py-12 text-slate-600">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500/30" />
              <p className="text-lg">No maintenance due this week</p>
              <p className="text-sm mt-1">All activities are up to date</p>
            </div>
          )}
        </div>
      )}

      {/* MACHINES VIEW */}
      {viewMode === 'machines' && (
        <div className="space-y-4">
          {(config?.sections || []).filter(s => !sectionFilter || s === sectionFilter).map(section => {
            const sectionMachines = machines.filter(m => m.section === section);
            if (sectionMachines.length === 0 && sectionFilter) return null;
            return (
              <div key={section} className="border border-slate-800 rounded-lg overflow-hidden">
                <div className="bg-slate-900/60 px-4 py-3 flex items-center justify-between border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-amber-400" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wide">{section}</h3>
                    <span className="text-xs text-slate-500">({sectionMachines.length})</span>
                  </div>
                </div>
                {sectionMachines.length > 0 ? (
                  <div className="divide-y divide-slate-800/50">
                    {sectionMachines.map(machine => (
                      <div key={machine.machine_id} className="px-4 py-3 hover:bg-slate-900/30" data-testid={`machine-${machine.machine_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 cursor-pointer" onClick={() => { setSelectedMachine(machine); fetchActivities(machine.machine_id); setViewMode('activities'); }}>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-bold">{machine.machine_name}</span>
                              {machine.machine_number && <span className="text-xs text-slate-500 font-mono">{machine.machine_number}</span>}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${machine.criticality === 'Critical' ? 'bg-red-500/20 text-red-400' : machine.criticality === 'High' ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-500/20 text-slate-400'}`}>{machine.criticality}</span>
                            </div>
                            <div className="flex gap-3 mt-1 text-xs text-slate-500">
                              {machine.manufacturer && <span>{machine.manufacturer} {machine.model}</span>}
                              {machine.location && <span>Loc: {machine.location}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openAddActivity(machine)} className="h-7 px-2 text-green-400 hover:bg-green-500/10" data-testid={`add-activity-${machine.machine_id}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </Button>
                            {isAdmin() && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEditMachine(machine)} className="h-7 px-2 text-cyan-400 hover:bg-cyan-500/10"><Edit className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeactivateMachine(machine)} className="h-7 px-2 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-center text-xs text-slate-600">No machines in this section</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ACTIVITIES VIEW for selected machine */}
      {viewMode === 'activities' && selectedMachine && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Button size="sm" variant="ghost" onClick={() => setViewMode('machines')} className="text-slate-400 mb-1">
                <ChevronRight className="w-3 h-3 mr-1 rotate-180" /> Back to Machines
              </Button>
              <h3 className="text-lg font-bold text-white">{selectedMachine.machine_name} <span className="text-slate-500 text-sm font-mono">{selectedMachine.machine_number}</span></h3>
              <p className="text-xs text-slate-500">{selectedMachine.section} | {selectedMachine.manufacturer} {selectedMachine.model}</p>
            </div>
            <Button size="sm" onClick={() => openAddActivity(selectedMachine)} className="bg-green-500/20 text-green-400 border border-green-500/50" data-testid="add-activity-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Activity
            </Button>
          </div>
          {activities.length > 0 ? (
            <div className="space-y-2">
              {activities.map(act => (
                <div key={act.activity_id} className={`border rounded-lg p-3 flex items-center justify-between ${act.status === 'Overdue' ? 'bg-red-950/20 border-red-500/20' : act.status === 'Due This Week' ? 'bg-orange-950/20 border-orange-500/20' : 'bg-slate-900/40 border-slate-800'}`} data-testid={`activity-${act.activity_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${act.status === 'Overdue' ? 'bg-red-500/20 text-red-400' : act.status === 'Due This Week' ? 'bg-orange-500/20 text-orange-400' : 'bg-cyan-500/20 text-cyan-400'}`}>{act.status}</span>
                      <span className="text-white font-bold text-sm">{act.activity_name}</span>
                    </div>
                    <div className="flex gap-3 mt-1 text-xs text-slate-500">
                      <span>Freq: {act.frequency}</span>
                      <span>Due: {act.next_due_date?.slice(0, 10) || 'Not set'}</span>
                      {act.last_maintenance_date && <span>Last: {act.last_maintenance_date?.slice(0, 10)}</span>}
                      {act.responsible_person && <span>Resp: {act.responsible_person}</span>}
                    </div>
                    {act.description && <p className="text-xs text-slate-600 mt-1">{act.description}</p>}
                  </div>
                  <Button size="sm" onClick={() => openComplete(act)} className="bg-green-500/20 text-green-400 border border-green-500/50 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Complete
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-600 text-sm">No maintenance activities. Click "Add Activity" to create one.</div>
          )}
        </div>
      )}

      {/* ADD/EDIT MACHINE DIALOG */}
      <Dialog open={showMachineDialog} onOpenChange={setShowMachineDialog}>
        <DialogContent className="bg-slate-950 border-amber-500/30 max-w-lg" data-testid="machine-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              {editingMachine ? 'Edit Machine' : 'Add Machine'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Machine Name *</Label>
                <Input value={machineForm.machine_name} onChange={e => setMachineForm(p => ({ ...p, machine_name: e.target.value }))} className="bg-slate-900/60 border-slate-800" data-testid="machine-name-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Asset/Machine Number</Label>
                <Input value={machineForm.machine_number} onChange={e => setMachineForm(p => ({ ...p, machine_number: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Section *</Label>
                <Select value={machineForm.section} onValueChange={val => setMachineForm(p => ({ ...p, section: val }))}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800" data-testid="machine-section-select"><SelectValue placeholder="Select section" /></SelectTrigger>
                  <SelectContent>{config?.sections?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Manufacturer</Label>
                <Input value={machineForm.manufacturer} onChange={e => setMachineForm(p => ({ ...p, manufacturer: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Model</Label>
                <Input value={machineForm.model} onChange={e => setMachineForm(p => ({ ...p, model: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Serial Number</Label>
                <Input value={machineForm.serial_number} onChange={e => setMachineForm(p => ({ ...p, serial_number: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Location</Label>
                <Input value={machineForm.location} onChange={e => setMachineForm(p => ({ ...p, location: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Criticality</Label>
                <Select value={machineForm.criticality} onValueChange={val => setMachineForm(p => ({ ...p, criticality: val }))}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.criticality_levels?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Notes</Label>
                <Textarea value={machineForm.notes} onChange={e => setMachineForm(p => ({ ...p, notes: e.target.value }))} className="bg-slate-900/60 border-slate-800 min-h-[50px]" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
              <Button variant="outline" onClick={() => setShowMachineDialog(false)} className="border-slate-700">Cancel</Button>
              <Button onClick={handleSaveMachine} className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase" data-testid="save-machine-btn">
                {editingMachine ? 'Save Changes' : 'Add Machine'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD ACTIVITY DIALOG */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="bg-slate-950 border-green-500/30 max-w-lg" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-400" />
              Add Maintenance Activity
            </DialogTitle>
          </DialogHeader>
          {selectedMachine && <p className="text-xs text-slate-500">Machine: <span className="text-white">{selectedMachine.machine_name}</span></p>}
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Activity Name *</Label>
                <Input value={activityForm.activity_name} onChange={e => setActivityForm(p => ({ ...p, activity_name: e.target.value }))} className="bg-slate-900/60 border-slate-800" placeholder="e.g. Oil Change, Belt Inspection" data-testid="activity-name-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Description</Label>
                <Textarea value={activityForm.description} onChange={e => setActivityForm(p => ({ ...p, description: e.target.value }))} className="bg-slate-900/60 border-slate-800 min-h-[50px]" placeholder="Work to be performed..." />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Frequency</Label>
                <Select value={activityForm.frequency} onValueChange={val => setActivityForm(p => ({ ...p, frequency: val }))}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800" data-testid="frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.frequencies?.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {activityForm.frequency === 'Custom' && (
                <div>
                  <Label className="text-xs text-slate-500 uppercase mb-1 block">Custom Interval (days)</Label>
                  <Input type="number" value={activityForm.custom_interval_days} onChange={e => setActivityForm(p => ({ ...p, custom_interval_days: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Next Due Date</Label>
                <Input type="date" value={activityForm.next_due_date} onChange={e => setActivityForm(p => ({ ...p, next_due_date: e.target.value }))} className="bg-slate-900/60 border-slate-800" data-testid="due-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Responsible Person</Label>
                <Input value={activityForm.responsible_person} onChange={e => setActivityForm(p => ({ ...p, responsible_person: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Estimated Downtime</Label>
                <Input value={activityForm.estimated_downtime} onChange={e => setActivityForm(p => ({ ...p, estimated_downtime: e.target.value }))} className="bg-slate-900/60 border-slate-800" placeholder="e.g. 2 hours" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Required Spares/Consumables</Label>
                <Input value={activityForm.required_spares} onChange={e => setActivityForm(p => ({ ...p, required_spares: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Safety Requirements</Label>
                <Input value={activityForm.safety_requirements} onChange={e => setActivityForm(p => ({ ...p, safety_requirements: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
              <Button variant="outline" onClick={() => setShowActivityDialog(false)} className="border-slate-700">Cancel</Button>
              <Button onClick={handleSaveActivity} className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase" data-testid="save-activity-btn">
                Add Activity
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* COMPLETE ACTIVITY DIALOG */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="bg-slate-950 border-green-500/30 max-w-md" data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              Complete Maintenance
            </DialogTitle>
          </DialogHeader>
          {selectedActivity && <p className="text-xs text-slate-400 mb-2">Activity: <span className="text-white font-bold">{selectedActivity.activity_name}</span></p>}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Completion Date *</Label>
                <Input type="date" value={completeForm.completion_date} onChange={e => setCompleteForm(p => ({ ...p, completion_date: e.target.value }))} className="bg-slate-900/60 border-slate-800" data-testid="complete-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Completed By *</Label>
                <Input value={completeForm.completed_by} onChange={e => setCompleteForm(p => ({ ...p, completed_by: e.target.value }))} className="bg-slate-900/60 border-slate-800" data-testid="completed-by-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase mb-1 block">Notes / Comments</Label>
              <Textarea value={completeForm.notes} onChange={e => setCompleteForm(p => ({ ...p, notes: e.target.value }))} className="bg-slate-900/60 border-slate-800 min-h-[50px]" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase mb-1 block">Parts / Consumables Used</Label>
              <Input value={completeForm.parts_used} onChange={e => setCompleteForm(p => ({ ...p, parts_used: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase mb-1 block">Downtime Experienced</Label>
              <Input value={completeForm.downtime_experienced} onChange={e => setCompleteForm(p => ({ ...p, downtime_experienced: e.target.value }))} className="bg-slate-900/60 border-slate-800" placeholder="e.g. 1.5 hours" />
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)} className="border-slate-700">Cancel</Button>
              <Button onClick={handleCompleteActivity} disabled={!completeForm.completion_date || !completeForm.completed_by} className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase disabled:opacity-50" data-testid="confirm-complete-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceModule;
